/**
 * ContentTable - Tabelweergave van content items met filtering en sortering
 */
import React, { useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, Paper, Chip, Select, MenuItem, FormControl,
  InputLabel, TextField, Autocomplete, IconButton, Tooltip,
} from '@mui/material';
import { FilterList as FilterIcon, Clear as ClearIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import ContentItemDetail from './ContentItemDetail';

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  IDEA: { label: 'Idee', color: '#6b7280', bg: '#f3f4f6' },
  CONCEPT: { label: 'Concept', color: '#8b5cf6', bg: '#ede9fe' },
  SHOOT_PLANNING: { label: 'Draaidag plannen', color: '#d97706', bg: '#fef3c7' },
  SHOOT_SCHEDULED: { label: 'Draaidag ingepland', color: '#ea580c', bg: '#ffedd5' },
  IN_PRODUCTION: { label: 'In Productie', color: '#2563eb', bg: '#dbeafe' },
  REVIEW: { label: 'Review', color: '#7c3aed', bg: '#ede9fe' },
  APPROVED: { label: 'Goedgekeurd', color: '#059669', bg: '#d1fae5' },
  ARCHIVED: { label: 'Gearchiveerd', color: '#9ca3af', bg: '#f3f4f6' },
};

const POST_STATUS_CONFIG = {
  INTERN: { label: 'Intern', color: '#6b7280' },
  CONCEPT: { label: 'Concept', color: '#8b5cf6' },
  SCHEDULED: { label: 'Ingepland', color: '#2563eb' },
  PUBLISHED: { label: 'Geplaatst', color: '#059669' },
  ARCHIVED: { label: 'Gearchiveerd', color: '#9ca3af' },
};

const APPROVAL_CONFIG = {
  NOT_OFFERED: { label: 'Nog niet', color: '#9ca3af' },
  IN_REVIEW: { label: 'In review', color: '#f59e0b' },
  APPROVED: { label: 'Goedgekeurd', color: '#10b981' },
  REJECTED: { label: 'Afgekeurd', color: '#ef4444' },
};

const CHANNELS = ['Instagram Post', 'Instagram Story', 'Instagram Reels', 'Facebook Post', 'TikTok', 'YouTube', 'YouTube Shorts', 'LinkedIn', 'Threads Post'];

// ─── Approval dot ───────────────────────────────────────────────────────────

function ApprovalDot({ value }) {
  const cfg = APPROVAL_CONFIG[value] || APPROVAL_CONFIG.NOT_OFFERED;
  return (
    <Tooltip title={cfg.label}>
      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: cfg.color, display: 'inline-block' }} />
    </Tooltip>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export default function ContentTable({ items = [], events = [], onRefresh }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [filterOwner, setFilterOwner] = useState('');
  const [filterTag, setFilterTag] = useState(null);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  // Sort
  const [orderBy, setOrderBy] = useState('plannedDate');
  const [order, setOrder] = useState('asc');

  const allOwners = useMemo(() => [...new Set(items.map((i) => i.ownerName).filter(Boolean))], [items]);
  const allTags = useMemo(() => [...new Set(items.map((i) => i.campaignTag).filter(Boolean))], [items]);

  const hasFilters = filterStatus || filterChannel || filterOwner || filterTag || filterDateFrom || filterDateTo;

  const clearFilters = () => {
    setFilterStatus(''); setFilterChannel(''); setFilterOwner('');
    setFilterTag(null); setFilterDateFrom(''); setFilterDateTo('');
  };

  const filtered = useMemo(() => {
    return items.filter((i) => {
      if (filterStatus && i.status !== filterStatus) return false;
      if (filterChannel && !(i.channels || []).includes(filterChannel)) return false;
      if (filterOwner && i.ownerName !== filterOwner) return false;
      if (filterTag && i.campaignTag !== filterTag) return false;
      if (filterDateFrom && (!i.plannedDate || i.plannedDate < filterDateFrom)) return false;
      if (filterDateTo && (!i.plannedDate || i.plannedDate > filterDateTo)) return false;
      return true;
    });
  }, [items, filterStatus, filterChannel, filterOwner, filterTag, filterDateFrom, filterDateTo]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const valA = a[orderBy] || '';
      const valB = b[orderBy] || '';
      const cmp = typeof valA === 'string' ? valA.localeCompare(valB) : (valA > valB ? 1 : -1);
      return order === 'asc' ? cmp : -cmp;
    });
  }, [filtered, orderBy, order]);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(field);
      setOrder('asc');
    }
  };

  const handleRowClick = (item) => {
    setDetailItem(item);
    setDrawerOpen(true);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '-';

  return (
    <Box>
      {/* Filter bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
            <MenuItem value="">Alle</MenuItem>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Kanaal</InputLabel>
          <Select value={filterChannel} label="Kanaal" onChange={(e) => setFilterChannel(e.target.value)}>
            <MenuItem value="">Alle</MenuItem>
            {CHANNELS.map((ch) => <MenuItem key={ch} value={ch}>{ch}</MenuItem>)}
          </Select>
        </FormControl>
        <Autocomplete
          options={allTags} value={filterTag} onChange={(_, v) => setFilterTag(v)}
          renderInput={(params) => <TextField {...params} label="Campagne" size="small" sx={{ minWidth: 160 }} />}
          size="small" freeSolo
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Eigenaar</InputLabel>
          <Select value={filterOwner} label="Eigenaar" onChange={(e) => setFilterOwner(e.target.value)}>
            <MenuItem value="">Alle</MenuItem>
            {allOwners.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField label="Vanaf" type="date" size="small" value={filterDateFrom}
          onChange={(e) => setFilterDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
        <TextField label="Tot" type="date" size="small" value={filterDateTo}
          onChange={(e) => setFilterDateTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
        {hasFilters && (
          <Tooltip title="Filters wissen">
            <IconButton size="small" onClick={clearFilters}><ClearIcon /></IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                { id: 'title', label: 'Titel' },
                { id: 'status', label: 'Status' },
                { id: 'postStatus', label: 'Post status' },
                { id: 'channels', label: 'Kanaal', sortable: false },
                { id: 'ownerName', label: 'Eigenaar' },
                { id: 'plannedDate', label: 'Plandatum' },
                { id: 'deadline', label: 'Deadline' },
              ].map((col) => (
                <TableCell key={col.id} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {col.sortable === false ? col.label : (
                    <TableSortLabel active={orderBy === col.id} direction={orderBy === col.id ? order : 'asc'} onClick={() => handleSort(col.id)}>
                      {col.label}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Concept</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Content</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Post</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 && (
              <TableRow><TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">Geen items gevonden</Typography>
              </TableCell></TableRow>
            )}
            {sorted.map((item) => {
              const sCfg = STATUS_CONFIG[item.status] || {};
              const pCfg = POST_STATUS_CONFIG[item.postStatus] || {};
              return (
                <TableRow key={item.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleRowClick(item)}>
                  <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                    {item.title || 'Naamloos'}
                  </TableCell>
                  <TableCell>
                    <Chip label={sCfg.label || item.status} size="small"
                      sx={{ bgcolor: sCfg.bg, color: sCfg.color, fontWeight: 600, fontSize: '0.7rem' }} />
                  </TableCell>
                  <TableCell>
                    {item.postStatus && (
                      <Chip label={pCfg.label || item.postStatus} size="small"
                        sx={{ bgcolor: `${pCfg.color}18`, color: pCfg.color, fontWeight: 600, fontSize: '0.7rem' }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(item.channels || []).slice(0, 2).map((ch) => (
                        <Chip key={ch} label={ch} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                      ))}
                      {(item.channels || []).length > 2 && <Chip label={`+${item.channels.length - 2}`} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />}
                    </Box>
                  </TableCell>
                  <TableCell><Typography variant="body2">{item.ownerName || '-'}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{formatDate(item.plannedDate)}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{formatDate(item.deadline)}</Typography></TableCell>
                  <TableCell sx={{ textAlign: 'center' }}><ApprovalDot value={item.approvalConcept} /></TableCell>
                  <TableCell sx={{ textAlign: 'center' }}><ApprovalDot value={item.approvalContent} /></TableCell>
                  <TableCell sx={{ textAlign: 'center' }}><ApprovalDot value={item.approvalPost} /></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {sorted.length} van {items.length} items
      </Typography>

      {/* Detail drawer */}
      <ContentItemDetail
        open={drawerOpen}
        item={detailItem}
        events={events}
        onClose={() => { setDrawerOpen(false); setDetailItem(null); }}
        onSaved={onRefresh}
      />
    </Box>
  );
}
